import React from "react";
import { useParams } from "react-router-dom";
import { apiGetAcc, apiJawabAcc, apiStatusAcc, accMockupUrl } from "@/lib/publicApi";
import { berubah, revisiBaruSiap, statusTeks, perluPantau, JEDA_MS, JUDUL_SIAP } from "@/lib/accPantau";
import * as W from "@/lib/waktu";
import { teksJawaban, VERSI_LAMA } from "@/lib/progres";
import { Loader2, ZoomIn, X, CheckCircle2, MessageSquareWarning, BellRing, PencilRuler, History, ExternalLink } from "lucide-react";
import GarisProgres from "@/components/GarisProgres";
import { AppCredit } from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";

// v2.19: a short beep (own Web Audio tone) — only after the customer touched the page (browsers block sound before)
function bunyi(ctx) {
  if (!ctx) return;
  try {
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.frequency.value = 880; o.type = "sine";
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.4);
  } catch { /* no sound on this device */ }
}

// F1.5-I: the customer checks the mockup and the order on the phone and
// approves it or asks for a revision. Only the newest mockup can be approved.
// v2.19 (owner 2026-10-26): the page checks itself about every 30 s (not while the tab is hidden) and shows
// "Desainer sedang mengerjakan revisi ke-N" / "Revisi ke-N sudah siap" without a reload.
// v2.20 (owner 2026-10-26): the progress line (Desain -> Revisi -> ACC customer -> Produksi -> Selesai), kept
// after the approval with a link to the order's status page, and the versions sent so far (v1.0, v1.1, ...):
// old ones open read-only, only the latest can be approved.
export default function AccCustomer() {
  const { token } = useParams();
  const [d, setD] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [besar, setBesar] = React.useState(false);
  const [f, setF] = React.useState({ nama: "", konfirmasi: false, catatan: "" });
  const [mode, setMode] = React.useState(null);  // null | "REVISI"
  const [busy, setBusy] = React.useState(false);
  const [hasil, setHasil] = React.useState(null);
  const [pesan, setPesan] = React.useState(null);
  const [lihat, setLihat] = React.useState(null);  // v2.20: an old version opened read-only

  const [siapBaru, setSiapBaru] = React.useState(null);  // the revision that just became ready (notice box)
  const load = React.useCallback(() => apiGetAcc(token).then((x) => { setD(x); setErr(null); return x; })
    .catch((e) => { setErr(e.response?.status === 404 ? "notfound" : "error"); return null; }), [token]);
  React.useEffect(() => { load(); }, [load]);

  // sound may only start after a touch / click / key on the page
  const audio = React.useRef(null);
  React.useEffect(() => {
    const sentuh = () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC && !audio.current) audio.current = new AC();
        if (audio.current?.state === "suspended") audio.current.resume();
      } catch { /* no Web Audio */ }
    };
    const ev = ["pointerdown", "keydown", "touchstart"];
    ev.forEach((e) => window.addEventListener(e, sentuh, { passive: true }));
    return () => ev.forEach((e) => window.removeEventListener(e, sentuh));
  }, []);

  // the check every ~30 s; paused while the tab is hidden, at once when it is shown again
  const dRef = React.useRef(null);
  dRef.current = d;
  const judulAsli = React.useRef(typeof document !== "undefined" ? document.title : "");
  React.useEffect(() => {
    let henti = false, jeda = 0;
    const cek = async () => {
      const lama = dRef.current;
      if (henti || jeda > 0 || document.visibilityState === "hidden" || !perluPantau(lama)) { jeda = Math.max(0, jeda - 1); return; }
      try {
        const st = await apiStatusAcc(token);
        if (henti || !berubah(lama, st)) return;
        const baru = await load();
        if (baru && revisiBaruSiap(lama, st)) {
          setSiapBaru(st.revisi); setHasil(null); setMode(null);
          document.title = JUDUL_SIAP;
          bunyi(audio.current);
        }
      } catch (e) {
        if (e.response?.status === 404) setErr("notfound");
        else if (e.response?.status === 429) jeda = 10;  // too many checks from this address: wait ±5 minutes
      }
    };
    const id = setInterval(cek, JEDA_MS);
    const tampil = () => { if (document.visibilityState === "visible") cek(); };
    document.addEventListener("visibilitychange", tampil);
    return () => { henti = true; clearInterval(id); document.removeEventListener("visibilitychange", tampil); };
  }, [token, load]);
  const tutupSiap = () => { setSiapBaru(null); document.title = judulAsli.current; };

  const kirim = async (aksi) => {
    setBusy(true); setPesan(null);
    try {
      const r = await apiJawabAcc(token, { aksi, nama: f.nama, konfirmasi: f.konfirmasi, catatan: f.catatan || null,
        versi_data: d.versi_data, versi_mockup: d.versi_mockup });
      setHasil(r.status);
      load();  // v2.19: the status line follows ("Desainer sedang mengerjakan revisi ke-N")
    } catch (e) {
      const det = e.response?.data?.detail;
      setPesan(typeof det === "string" ? det : Array.isArray(det) ? det.map((x) => x.msg).join(" ") : "Gagal mengirim. Coba lagi.");
      if (e.response?.status === 409) load();
    } finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-white p-8 text-center text-slate-600">{err === "notfound" ? "Link ACC tidak ditemukan, sudah kedaluwarsa, atau dicabut. Hubungi kami untuk link baru." : "Halaman tidak dapat dimuat. Coba lagi nanti."}</div>;
  if (!d) return <div className="min-h-screen bg-white p-8 text-center text-slate-500"><Loader2 className="inline animate-spin" /> Memuat…</div>;

  const selesai = hasil || d.sudah_diproses || d.status === "DISETUJUI";
  // owner 2026-10-15 (A5): a Jersey mockup may come in labelled pictures (full view, logo, chest, collar, sleeve)
  const slot = d.mockup_slot || [];
  const utama = slot.find((s) => s.slot === "PENUH") || slot[0] || null;
  const lain = slot.filter((s) => s !== utama);
  const nDesain = Math.max(d.jumlah_desain || 1, ...slot.map((s) => s.desain_ke || 1));  // v2.21
  const src = (v) => (v ? accMockupUrl(token, v) : `${accMockupUrl(token)}?v=${d.versi_mockup}`);
  const garis = statusTeks(d, (x) => W.waktu(x));
  const rv = d.riwayat_versi || [];
  const terbaru = rv.find((x) => x.terbaru);
  return (
    <div data-testid="acc-page" className="min-h-screen bg-slate-100 text-slate-900">
      <PublicHeader judul="Persetujuan desain" sub={`${d.nomor_faktur} · ${d.customer_nama}`} namaUsaha={d.nama_usaha} testid="acc-header" />
      <main className="relative -mt-8 mx-auto max-w-xl px-3 pb-4 space-y-4">
        {siapBaru && (
          <div data-testid="acc-revisi-baru" role="status" className="rounded-xl border-2 border-emerald-500 bg-emerald-50 p-3 text-emerald-900 shadow-sm">
            <div className="flex items-start gap-2">
              <BellRing className="mt-0.5 shrink-0" size={20} />
              <div className="flex-1 text-sm"><b>Revisi ke-{siapBaru.ke} sudah siap.</b> Mockup terbaru sudah tampil di bawah. Silakan periksa, lalu setujui atau minta revisi.</div>
              <button type="button" data-testid="acc-revisi-baru-tutup" onClick={tutupSiap} aria-label="Tutup" className="rounded p-1 hover:bg-emerald-100"><X size={18} /></button>
            </div>
          </div>
        )}
        {garis && (
          <div data-testid="acc-status-revisi" className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold shadow-sm ${d.revisi?.siap ? "bg-emerald-600 text-white" : "bg-amber-100 text-amber-900 border border-amber-300"}`}>
            {d.revisi?.siap ? <CheckCircle2 size={18} className="shrink-0" /> : <PencilRuler size={18} className="shrink-0" />}<span>{garis}</span>
          </div>
        )}
        {d.progres && (
          <section data-testid="acc-progres" className="rounded-xl bg-white p-3 shadow-sm">
            <div className="mb-2 text-[15px] font-bold text-soq-biru-tua">Progres pesanan</div>
            <GarisProgres progres={d.progres} untuk="customer" testid="acc-garis" />
          </section>
        )}
        <section className="rounded-xl bg-white p-3 border-t-4 border-t-amber-500 shadow-[0_8px_24px_rgba(16,32,208,0.08)]">
          {d.mockup ? (
            <button data-testid="acc-mockup" onClick={() => setBesar(utama?.versi || true)} className="relative block w-full">
              <img src={src(utama?.versi)} alt={`Mockup versi ${d.versi_mockup}`} className="w-full rounded-lg" />
              {utama && lain.length > 0 && <span className="absolute left-2 top-2 rounded bg-white/90 px-2 py-0.5 text-xs font-semibold text-soq-biru-tua">{utama.label}</span>}
              <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded bg-black/60 px-2 py-1 text-xs text-white"><ZoomIn size={14} /> Perbesar</span>
            </button>
          ) : <div className="p-6 text-center text-slate-500">Mockup belum tersedia. Kami akan mengirim link ini lagi saat mockup siap.</div>}
          {lain.length > 0 && nDesain <= 1 && (
            <div data-testid="acc-mockup-slot" className="mt-2 grid grid-cols-3 gap-2">
              {lain.map((s) => (
                <button key={s.versi} data-testid={`acc-slot-${s.slot}`} onClick={() => setBesar(s.versi)} className="rounded-lg border border-slate-300 bg-white p-1 text-center">
                  <img src={src(s.versi)} alt={s.label} className="mx-auto h-24 w-full object-contain" />
                  <span className="block text-xs font-semibold text-soq-biru-tua">{s.label}</span>
                </button>
              ))}
            </div>
          )}
          {/* v2.21: one nota with several designs = the pictures of each design together; one approval for all */}
          {nDesain > 1 && Array.from({ length: nDesain }, (_, i) => i + 1).map((k) => {
            const isi = slot.filter((s) => (s.desain_ke || 1) === k);
            return (
              <div key={k} data-testid={`acc-desain-${k}`} className="mt-3">
                <div className="text-sm font-bold text-soq-biru-tua">Desain {k} dari {nDesain}</div>
                {isi.length ? (
                  <div className="mt-1 grid grid-cols-3 gap-2">
                    {isi.map((s) => (
                      <button key={s.versi} data-testid={`acc-slot-${k}-${s.slot}`} onClick={() => setBesar(s.versi)} className="rounded-lg border border-slate-300 bg-white p-1 text-center">
                        <img src={src(s.versi)} alt={s.label} className="mx-auto h-24 w-full object-contain" />
                        <span className="block text-xs font-semibold text-soq-biru-tua">{s.label}</span>
                      </button>
                    ))}
                  </div>
                ) : <p className="mt-1 text-xs text-slate-500">Mockup desain ini belum tersedia.</p>}
              </div>
            );
          })}
          {d.mockup && <div className="mt-1 text-center text-xs text-slate-500">Mockup {terbaru ? `${terbaru.label} (terbaru)` : `versi ${d.versi_mockup}`}{lain.length > 0 ? ` · ${slot.length} gambar — periksa semuanya` : ""}</div>}
        </section>

        {rv.length > 1 && (
          <section data-testid="acc-riwayat-versi" className="rounded-xl bg-white p-4 shadow-sm text-sm">
            <div className="mb-2 flex items-center gap-2 text-[15px] font-bold text-soq-biru-tua"><History size={18} /> Riwayat versi</div>
            <ul className="divide-y divide-slate-200">
              {rv.map((v) => (
                <li key={v.versi_mockup} data-testid={`acc-versi-${v.label}`} className="flex items-start gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-bold text-slate-900">{v.label}</span>
                      {v.terbaru && <span className="rounded bg-emerald-100 px-1.5 text-xs font-semibold text-emerald-800">terbaru</span>}
                      {v.dikirim && <span className="text-xs text-slate-500">dikirim {W.waktu(v.dikirim)}</span>}
                    </div>
                    {v.jawaban.map((j, i) => <div key={i} className={`mt-0.5 break-words ${j.status === "REVISI" ? "text-amber-800" : "text-emerald-800"}`}>{teksJawaban(j)}</div>)}
                  </div>
                  {!v.terbaru && <button type="button" data-testid={`acc-lihat-${v.label}`} onClick={() => setLihat(v)} className="shrink-0 min-h-[40px] rounded-lg border border-slate-400 px-3 text-sm font-semibold text-soq-biru-tua hover:bg-slate-50">Lihat</button>}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="rounded-xl bg-white p-4 shadow-sm border border-slate-800 text-sm space-y-1">
          <div className="text-[15px] font-bold text-soq-biru-tua">Pesanan</div>
          {d.items.map((it, i) => <div key={i}>{nDesain > 1 && <span data-testid={`acc-item-desain-${i}`} className="mr-1 rounded bg-soq-biru/10 px-1 text-xs font-semibold text-soq-biru-tua">Desain {it.desain_ke || 1}</span>}{it.nama} · {it.qty} {it.satuan}{it.detail ? <span className="text-slate-500"> · {it.detail}</span> : null}</div>)}
          {d.jersey && <>
            <div className="pt-2 font-bold text-soq-biru-tua">Ukuran</div>
            <div className="flex flex-wrap gap-2">{Object.entries(d.jersey.ukuran).filter(([, q]) => q).map(([u, q]) => <span key={u} className="rounded-md border border-amber-500/40 bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">{u}: {q}</span>)}</div>
            <div className="pt-2 font-bold text-soq-biru-tua">Nama & nomor ({d.jersey.pemain.length})</div>
            <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-500"><th>Nama</th><th>No</th><th>Ukuran</th><th>Lengan</th></tr></thead>
              <tbody>{d.jersey.pemain.map((p, i) => <tr key={i} className="border-t"><td>{p.nama}</td><td>{p.nomor}</td><td>{p.ukuran}</td><td>{p.lengan}</td></tr>)}</tbody></table>
          </>}
        </section>

        {selesai ? (
          <div data-testid="acc-selesai" className="rounded-xl bg-emerald-50 p-4 text-center text-emerald-800">
            <CheckCircle2 className="inline mb-1" /> {hasil === "REVISI" ? "Permintaan revisi terkirim. Halaman ini memberi tahu Anda saat revisi siap — tidak perlu dimuat ulang." : "Desain sudah disetujui. Terima kasih, pesanan akan diproses."}
            {hasil !== "REVISI" && d.tracking_url && (
              <a data-testid="acc-lihat-status" href={d.tracking_url} className="mt-3 flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-soq-biru px-4 font-semibold text-white">
                <ExternalLink size={16} /> Lihat status pesanan
              </a>
            )}
          </div>
        ) : (
          <section className="rounded-xl bg-white p-4 border-t-4 border-t-soq-biru shadow-sm space-y-3">
            {d.status === "REVISI" && d.catatan_revisi && <div className="rounded bg-amber-50 p-2 text-sm text-amber-800">Permintaan revisi Anda: {d.catatan_revisi} — menunggu mockup baru.</div>}
            <div data-testid="acc-peringatan" className="rounded border-l-4 border-l-red-600 bg-red-50 p-3 text-sm text-red-800">{d.peringatan}</div>
            <label className="block text-sm">Nama Anda
              <input data-testid="acc-nama" value={f.nama} onChange={(e) => setF({ ...f, nama: e.target.value })} className="mt-1 w-full min-h-[44px] rounded-lg border border-slate-600 px-3 py-2 focus:border-soq-biru focus:outline-none focus:ring-2 focus:ring-soq-biru/25" /></label>
            {mode === "REVISI" ? (<>
              <label className="block text-sm">Yang perlu direvisi
                <textarea data-testid="acc-catatan" value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} rows={3} className="mt-1 w-full min-h-[44px] rounded-lg border border-slate-600 px-3 py-2 focus:border-soq-biru focus:outline-none focus:ring-2 focus:ring-soq-biru/25" /></label>
              <div className="flex gap-2">
                <button data-testid="acc-kirim-revisi" disabled={busy} onClick={() => kirim("REVISI")} className="flex-1 rounded-lg bg-amber-500 py-3 font-semibold text-slate-950 disabled:opacity-50"><MessageSquareWarning className="inline" size={16} /> Kirim permintaan revisi</button>
                <button onClick={() => setMode(null)} className="rounded-lg border px-3">Batal</button>
              </div>
            </>) : (<>
              <label className="flex items-start gap-2 text-sm"><input data-testid="acc-konfirmasi" type="checkbox" checked={f.konfirmasi} onChange={(e) => setF({ ...f, konfirmasi: e.target.checked })} className="mt-1 h-5 w-5" />
                Saya sudah memeriksa mockup, nama, nomor, ukuran, warna dan jumlah, dan menyetujuinya.</label>
              <button data-testid="acc-setuju" disabled={busy || !f.konfirmasi || f.nama.trim().length < 2 || !d.mockup} onClick={() => kirim("SETUJU")} className="w-full rounded-lg bg-emerald-600 py-3 text-lg font-semibold text-white disabled:opacity-40">Setuju (ACC)</button>
              <button data-testid="acc-revisi" onClick={() => setMode("REVISI")} className="w-full min-h-[44px] rounded-lg border-2 border-amber-500 py-2 font-semibold text-amber-700">Minta revisi</button>
            </>)}
            {pesan && <div data-testid="acc-pesan" className="text-sm text-red-700">{pesan}</div>}
          </section>
        )}
        <AppCredit className="pt-6 pb-4" />
      </main>
      {lihat && (
        <div data-testid="acc-versi-lama" role="dialog" aria-label={`Versi ${lihat.label}`} className="fixed inset-0 z-50 overflow-auto bg-slate-900/90 p-3">
          <div className="mx-auto max-w-xl rounded-xl bg-white p-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 text-[15px] font-bold text-soq-biru-tua">{lihat.label}{lihat.dikirim ? <span className="ml-2 text-xs font-normal text-slate-500">dikirim {W.waktu(lihat.dikirim)}</span> : null}</div>
              <button type="button" data-testid="acc-versi-lama-tutup" onClick={() => setLihat(null)} aria-label="Tutup" className="rounded-full p-2 hover:bg-slate-100"><X /></button>
            </div>
            <div className="my-2 rounded border-l-4 border-l-amber-500 bg-amber-50 p-2 text-sm font-semibold text-amber-900">{VERSI_LAMA}</div>
            {lihat.jawaban.map((j, i) => <div key={i} className="mb-2 break-words text-sm text-slate-700">{teksJawaban(j)}</div>)}
            <div className="space-y-2">
              {lihat.slot.map((s) => (
                <figure key={s.versi} className="rounded-lg border border-slate-300 p-1">
                  <img src={src(s.versi)} alt={`${lihat.label} · ${s.label}`} className="w-full rounded" />
                  {lihat.slot.length > 1 && <figcaption className="pt-1 text-center text-xs font-semibold text-soq-biru-tua">{s.label}</figcaption>}
                </figure>
              ))}
            </div>
          </div>
        </div>
      )}
      {besar && (
        <div data-testid="acc-zoom" className="fixed inset-0 z-50 bg-black/95 overflow-auto" style={{ touchAction: "pinch-zoom pan-x pan-y" }}>
          <button onClick={() => setBesar(false)} className="fixed right-3 top-3 z-10 rounded-full bg-white/90 p-2"><X /></button>
          <img src={src(typeof besar === "number" ? besar : null)} alt="Mockup ukuran penuh" className="max-w-none" style={{ width: "200%" }} />
        </div>
      )}
    </div>
  );
}
